import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import isEmpty from 'lodash/isEmpty';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import { setModulePollingList } from '../../../../store/investor/action';
import { unlockOrRefreshReport } from '../../../../store/investor/investorApiHelper';
import { TAB_TO_TASK_TRACKER_MAP } from '../../../../utils/constants/commonConstants';

import { Button, CheckBox } from '../../../../common/_custom';
import SideDrawer from '../../../../common/_custom/SideDrawer/SideDrawer';
import SideDrawerFooter from '../../../../common/_custom/SideDrawer/components/SideDrawerFooter';

import greyLock from '../../../../assets/grey-lock-2.svg';
import styles from './RefreshReport.module.scss';
import { fetchCompanyOverviewData } from '../../../../store/dashboard/overview/actions';
import { updateQueryParams } from '../../../../utils/searchParamUtils';
import { getSubdomain } from '../../../../utils/utils';
import { AICA_UNLOCK_REFRESH_UPDATE } from '../../../../utils/constants/mixpanelEvents/investorEvents';
import { Mixpanel } from '../../../../utils/mixpanel';
import LoadingSpinner from '../../../../common/_custom/LoadingSpinner/LoadingSpinner';
import { useMcaPolling } from '../../../../utils/customHooks';
import { generateCompanyOverview } from '../../../../store/investor/apiHelpers';
import { OverviewData } from '../../../../store/dashboard/overview/types';

dayjs.extend(relativeTime);

type Props = {
    close: () => void;
    onUnlockReportSuccess: () => void;
    selectedCompany: any;
};

const RefreshReport: React.FC<Props> = ({ close, onUnlockReportSuccess, selectedCompany }) => {
    const dispatch = useAppDispatch();
    const params = useParams();

    const [overviewData, setOverviewData] = useState<OverviewData | null>(null);

    const orgId: string = params.id as string;
    const user = useAppSelector((state) => state.user);
    const dashboardConfig: any = useAppSelector((state) => state.investor.config);
    const permissions = useAppSelector((state) => state.investor.permissions);
    const tabLockStatus = useAppSelector((state) => state.investor.tabLockStatus);
    const taskTrackerMap = useAppSelector((state) => state.investor.taskTrackerMap);
    const modulePollingList = useAppSelector((state) => state.investor.modulePollingList);
    const investorData = useAppSelector((state) => state.investor);
    const showNewOverview =
        useAppSelector((state) => state.dashboard.dashboardReducer.config?.tabConfigs?.overview?.showNewOverview) || false;
    const selectedCompanyFromRedux = useAppSelector((state) => state.investor.selectedCompany);
    const profileGenerationStatus = investorData?.selectedCompany?.profileGenerationStatus;

    const { mcaDocData } = useMcaPolling(orgId);

    const onboardingTabs = permissions?.onboardingTabs;
    const isProfileLocked = profileGenerationStatus === 'LOCKED';
    const allRefreshableModules = dashboardConfig?.tabs?.filter((tab: any) => tab.refreshableModule === true);
    const primaryModuleRefreshList =
        dashboardConfig?.tabs?.filter((tab: any) => tab.refreshableModule === true && tab.category === 'primary') || [];
    const [unlocking, setUnlocking] = useState(false);
    const [selectedModules, setSelectedModules] = useState<Set<string>>(new Set());
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();

    // Get overallStatus from Redux state instead of making API call
    const overallStatus = selectedCompanyFromRedux?.overallStatus || '';

    const isTabLocked = useCallback(
        (tabId: string) => {
            // Priority check: If overview tab and overallStatus is LOCKED, always lock the overview tab
            if (tabId === 'overview' && overallStatus === 'LOCKED') {
                return true;
            }

            if (primaryModuleRefreshList.some((tab) => tab.id === tabId)) return !!tabLockStatus?.[tabId]?.isLocked;
            let anyStepCompleteOrFailed = false;
            if (!isEmpty(taskTrackerMap)) {
                const taskTrackerSteps = TAB_TO_TASK_TRACKER_MAP[tabId]
                    .filter((step) => step in taskTrackerMap)
                    .map((step) => taskTrackerMap[step].status);
                anyStepCompleteOrFailed = isEmpty(taskTrackerSteps)
                    ? false
                    : taskTrackerSteps.some((step) => step === 'COMPLETE' || step === 'FAILED');
            }
            return !!(
                (isProfileLocked && !onboardingTabs?.includes(tabId)) ||
                !anyStepCompleteOrFailed ||
                tabLockStatus?.[tabId]?.isLocked
            );
        },
        [isProfileLocked, onboardingTabs, taskTrackerMap, tabLockStatus, overallStatus],
    );

    useEffect(() => {
        dispatch(
            fetchCompanyOverviewData({
                assesseeOrgId: orgId,
                investorOrgId: user.domainMetadata?.investorOrgId ?? '',
                onSuccess: (data: OverviewData, actionCta) => {
                    setOverviewData(data);
                },
            }),
        );
    }, [orgId, user.domainMetadata?.investorOrgId]);

    const handleModuleSelection = (module: string) => {
        const newSelectedModules = new Set<string>(selectedModules);

        if (module === 'all') {
            if (selectedModules.size === allRefreshableModules.length) {
                newSelectedModules.clear();
            } else {
                allRefreshableModules.forEach((module) => newSelectedModules.add(module.id));
            }
        } else if (selectedModules.has(module)) {
            newSelectedModules.delete(module);
        } else {
            newSelectedModules.add(module);
        }

        setSelectedModules(newSelectedModules);
    };

    const onPrimary = () => {
        Mixpanel.track(AICA_UNLOCK_REFRESH_UPDATE, {
            userRole: user?.userData?.accountType,
            pnoPlatform: getSubdomain(),
            userId: user?.userData?.userId,
            currentUrl: window.location.href,
            organizationId: orgId,
            modulesForUnlock: Array.from(selectedModules).filter((module) => isTabLocked(module)),
            modulesForRefresh: Array.from(selectedModules).filter((module) => !isTabLocked(module)),
            modulesCountForUpdate: selectedModules.size,
        });

        setUnlocking(true);

        if (selectedModules.has('overview') && showNewOverview) {
            const payload = {
                legalName: selectedCompany?.orgName as string,
                website: (selectedCompany?.website as string) || (overviewData?.socialInfo?.websiteUrl as string) || '',
                cin: (selectedCompany?.cin as string) || (overviewData?.cinNumber as string) || '',
                pan: (selectedCompany?.pan as string) || (overviewData?.panNumber as string) || '',
                orgId: orgId,
            };

            try {
                generateCompanyOverview(payload);

                // Set the refreshOverviewV2 param to trigger data reset and polling for overviewV2
                searchParams.set('refreshOverviewV2', 'true');
                updateQueryParams(searchParams, setSearchParams, {}, location);
            } catch (error) {
                console.error('Failed to generate company overview:', error);
            }
        }

        unlockOrRefreshReport(
            {
                assesseeOrgId: orgId,
                investorOrgId: user.domainMetadata?.investorOrgId ?? '',
                tabs: Array.from(selectedModules),
            },
            {
                onSuccess: () => {
                    setUnlocking(false);
                    dispatch(setModulePollingList(Array.from(selectedModules)));
                    if (profileGenerationStatus === 'LOCKED') {
                        onUnlockReportSuccess();
                    }
                    dispatch(
                        fetchCompanyOverviewData({
                            assesseeOrgId: orgId,
                            investorOrgId: user.domainMetadata?.investorOrgId ?? '',
                            onSuccess: () => {
                                updateQueryParams(searchParams, setSearchParams, { triggerMain: true }, location);
                            },
                            onError: () => {},
                        }),
                    );
                    close();
                },
                onError: () => {
                    setUnlocking(false);
                },
            },
        ).finally(() => {
            setUnlocking(false);
        });
    };

    const renderModuleSelectionTable = useMemo(() => {
        return (
            <table className={styles.ModuleSelectionTable}>
                <thead className={styles.ModuleSelectionTableHead}>
                    <tr>
                        <th style={{ width: '3.375rem' }}>
                            <CheckBox
                                checked={selectedModules.size === allRefreshableModules.length}
                                label=""
                                onCheck={() => handleModuleSelection('all')}
                                variant="primary"
                            />
                        </th>
                        <th style={{ width: '15.438rem' }}>List of modules</th>
                        <th
                            style={{
                                textAlign: 'end',
                                paddingRight: '1rem',
                            }}
                        >
                            Last fetched
                        </th>
                    </tr>
                </thead>

                <tbody className={styles.ModuleSelectionTableBody}>
                    {allRefreshableModules.map((module) => {
                        let lastFetchedInWords = '-';
                        const moduleTasks = TAB_TO_TASK_TRACKER_MAP[module.id] || [];

                        if (taskTrackerMap && moduleTasks) {
                            // Special case for overview: only show date for specific relevant tasks
                            if (module.id === 'overview') {
                                const overviewRelevantTasks = ['AION_FETCH_FUNDING_HISTORY', 'AION_FETCH_SHAREHOLDERS_DATA'];
                                const lastFetched = overviewRelevantTasks
                                    .filter((task) => taskTrackerMap[task]?.status === 'COMPLETE')
                                    .map((task) => taskTrackerMap[task]?.dateCreated)
                                    .filter(Boolean)
                                    .sort((a, b) => b - a)[0];
                                lastFetchedInWords = lastFetched ? dayjs(lastFetched).fromNow() : '-';
                            } else {
                                // For all other modules: use existing logic (most recent among all tasks)
                                const lastFetched = moduleTasks
                                    .filter((task) => taskTrackerMap[task]?.status === 'COMPLETE')
                                    .map((task) => taskTrackerMap[task]?.dateCreated)
                                    .filter(Boolean)
                                    .sort((a, b) => b - a)[0];
                                lastFetchedInWords = lastFetched ? dayjs(lastFetched).fromNow() : '-';
                            }
                        }

                        const isFinancialsModule = module.id === 'auditedFinancials';
                        const isProcessing =
                            isFinancialsModule && mcaDocData?.reportStatus === 'Processing' && mcaDocData?.isFetching === true;

                        return (
                            <tr key={module.id}>
                                <td style={{ width: '3.375rem' }}>
                                    <CheckBox
                                        checked={selectedModules.has(module.id)}
                                        label=""
                                        onCheck={() => handleModuleSelection(module.id)}
                                        variant="primary"
                                    />
                                </td>
                                <td>
                                    <div className={styles.ModuleName}>
                                        {module.label}{' '}
                                        {isTabLocked(module?.id as string) && !modulePollingList.includes(module?.id as string) && (
                                            <img className={styles.LockIcon} src={greyLock} alt="" height="16px" />
                                        )}
                                        {isProcessing && <LoadingSpinner color="#6021b3" height="15px" className={styles.InlineSpinner} />}
                                    </div>
                                </td>
                                <td className={styles.LastFetched}>
                                    {isProcessing ? (
                                        <span className={styles.FetchingText}>Fetching latest data from MCA (takes upto 24 hours)</span>
                                    ) : (
                                        lastFetchedInWords
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        );
    }, [allRefreshableModules, selectedModules, taskTrackerMap, mcaDocData]);

    return (
        <SideDrawer
            showHeading
            heading={<div className={styles.DrawerHeader}>Unlock/Refresh Report</div>}
            onClose={() => close()}
            width="44.438rem"
        >
            <div className={styles.RefreshReportContainer} style={{ height: '100%' }}>
                <div className={styles.RefreshReportContent}>
                    {renderModuleSelectionTable}
                    <p className={styles.RefrestNote}>
                        User consent-based data from GST, Debt Schedule etc. will be updated automatically when available.
                    </p>
                </div>

                <SideDrawerFooter>
                    <div className={styles.FooterContainer}>
                        <Button
                            style={{ marginTop: 0 }}
                            type="submit"
                            disabled={selectedModules.size === 0}
                            loading={unlocking}
                            onClick={onPrimary}
                            variant="primary"
                            text="Update"
                        />
                        <Button style={{ marginTop: 0 }} type="submit" onClick={close} variant="secondary" text="Cancel" />
                    </div>
                </SideDrawerFooter>
            </div>
        </SideDrawer>
    );
};

export default RefreshReport;
